import _ from 'lodash';
import { Avatar, Icon, MemberContactPopover, PlaceholderAvatar, PlaceholderPopover, Tooltip } from '~/components';
import React from 'react';
import styled from 'styled-components';
import { colors } from '~/styles';
import { Byline, Info, Label, Resource } from '../../components/schedule/sidebar';
import { Action, Actions, Box, Content, Row as RowContainer } from '../../components/schedule/sidebar/SidebarStyles';
import SidebarMenu from '../../components/schedule/sidebar/SidebarMenu';

const RowActions = styled(Actions)`
  flex-direction: row;
`;

const RowAction = styled(Action)`
  width: 1rem;
  margin-left: 0.5rem;
`;

const WarningIndicator = styled.div`
  display: flex;
  align-items: center;
`;

export default function Row({ row, onReassignAllocations, onSetBookingType }) {
  if (!row.resource) return null;

  const showUnassignedWarning = row.allocations?.some((allocation) => allocation.isProjectMember === false);

  return (
    <RowContainer key={row.id} style={row.meta.style} data-testid={`row_${row.id}`} data-testid-lanes={row.meta.lanes}>
      <Box>
        <Content>
          <Resource>
            {
              {
                placeholder: (
                  <PlaceholderPopover placeholderId={row.resource.id} placement="right">
                    <PlaceholderAvatar
                      value={row.resource}
                      hasBackground
                      isCircle
                      size="1.75rem"
                      initialsFontSize=".75rem"
                    />
                  </PlaceholderPopover>
                ),
                member: (
                  <MemberContactPopover member={row.resource} placement="right">
                    <Avatar value={row.resource} hasBackground isCircle size="1.75rem" initialsFontSize=".75rem" />
                  </MemberContactPopover>
                ),
              }[row.resource.resourceType]
            }

            <Info style={{ marginLeft: '.5rem' }}>
              <Label>{row.resource.name}</Label>
              {row.resource.practice && <Byline>{row.resource.practice.name}</Byline>}
            </Info>
          </Resource>
        </Content>

        {showUnassignedWarning && (
          <WarningIndicator>
            <Tooltip message="This member is not currently assigned to this project.">
              <Icon icon="exclamation-triangle" color={colors.warning} />
            </Tooltip>
          </WarningIndicator>
        )}

        {(_.isFunction(onReassignAllocations) || _.isFunction(onSetBookingType)) && (
          <RowActions>
            <RowAction>
              <SidebarMenu>
                {({ setIsOpen }) => {
                  const handleReassignAllocations = async () => {
                    setIsOpen(false);
                    await onReassignAllocations(row.project, row.resource);
                  };

                  const handleSetBookingType = (bookingTypeId) => async () => {
                    setIsOpen(false);
                    await onSetBookingType(row.project, row.resource, bookingTypeId);
                  };

                  return (
                    <>
                      {_.isFunction(onReassignAllocations) && (
                        <SidebarMenu.Item
                          disabled={!row.project.permissions.manageAllocations}
                          onClick={handleReassignAllocations}
                          tooltip={
                            !row.project.permissions.manageAllocations
                              ? 'Insufficient permissions to manage allocations on this project.'
                              : undefined
                          }>
                          Reassign Allocations
                        </SidebarMenu.Item>
                      )}
                      {_.isFunction(onSetBookingType) && (
                        <>
                          <SidebarMenu.Item
                            disabled={!row.project.permissions.manageAllocations}
                            onClick={handleSetBookingType('soft')}
                            tooltip={
                              !row.project.permissions.manageAllocations
                                ? 'Insufficient permissions to manage allocations on this project.'
                                : undefined
                            }>
                            Set Allocations to Soft
                          </SidebarMenu.Item>
                          <SidebarMenu.Item
                            disabled={!row.project.permissions.manageAllocations}
                            onClick={handleSetBookingType('confirmed')}
                            tooltip={
                              !row.project.permissions.manageAllocations
                                ? 'Insufficient permissions to manage allocations on this project.'
                                : undefined
                            }>
                            Set Allocations to Confirmed
                          </SidebarMenu.Item>
                        </>
                      )}
                    </>
                  );
                }}
              </SidebarMenu>
            </RowAction>
          </RowActions>
        )}
      </Box>
    </RowContainer>
  );
}
